import { Link } from 'react-router-dom'

import { Eye24 } from '@makyoapp/makyo-icon/icon'

import { useGetStorageMetric } from '@/resolvers/org/storage/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { ORG_APP_ROUTE, ORG_MEMBER_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { getBytesBySize } from '@/shared/utils'

import { ReactComponent as IconHardDrive } from '@/assets/vectors/icon_lucide_hard_drive.svg?react'
import { ReactComponent as IconLayout } from '@/assets/vectors/icon_lucide_layout.svg?react'
import { ReactComponent as IconUsers } from '@/assets/vectors/icon_lucide_users.svg?react'

function QuickStatsSection() {
  const { currentOrg } = useCurrentOrgContext()
  const { data: storageMetric } = useGetStorageMetric()

  const renderProgressBar = (value: string, total: string, color: string, lightColor: string) => {
    const percentage = total === '∞' ? 60 : (parseInt(value) / parseInt(total)) * 100
    return (
      <div className='h-1 bg-slate-200 dark:bg-slate-700 rounded-full'>
        <div className={`h-full ${lightColor} dark:${color} rounded-full`} style={{ width: `${percentage}%` }} />
      </div>
    )
  }

  return (
    <div className='grid grid-cols-4 gap-4 mb-8'>
      <Link
        to={`${ORG_ROUTE}/${currentOrg?.id}/${ORG_APP_ROUTE}`}
        className='text-sm text-slate-700 dark:text-slate-300 mb-3 hover:text-slate-900 dark:hover:text-white transition-colors'
      >
        <div className='bg-white dark:bg-slate-800/50 rounded-xl p-4 backdrop-blur-xs border border-slate-200/50 dark:border-slate-700/50 shadow-xs'>
          <div className='flex justify-between items-start mb-3'>
            <span className='p-2 rounded-lg bg-pink-50 dark:bg-pink-500/10 fill-none text-pink-600 dark:text-pink-400'>
              <IconLayout className='h-5 w-5 fill-transparent' />
            </span>
            <span className='text-xs text-slate-600 dark:text-slate-400'>5/5</span>
          </div>
          <p className='text-sm text-slate-700 dark:text-slate-300 mb-3'> Published Apps</p>
          {renderProgressBar('5', '5', 'bg-pink-500', 'bg-pink-600')}
        </div>
      </Link>

      <Link
        to={`${ORG_ROUTE}/${currentOrg?.id}/${ORG_MEMBER_ROUTE}`}
        className='text-sm text-slate-700 dark:text-slate-300 mb-3 hover:text-slate-900 dark:hover:text-white transition-colors'
      >
        <div className='bg-white dark:bg-slate-800/50 rounded-xl p-4 backdrop-blur-xs border border-slate-200/50 dark:border-slate-700/50 shadow-xs'>
          <div className='flex justify-between items-start mb-3'>
            <span className='p-2 rounded-lg bg-blue-50 dark:bg-blue-500/10 fill-none text-blue-600 dark:text-blue-400'>
              <IconUsers className='h-5 w-5 fill-transparent' />
            </span>
            <span className='text-xs text-slate-600 dark:text-slate-400'>{currentOrg?.members.length || 0}/∞</span>
          </div>
          <p className='text-sm text-slate-700 dark:text-slate-300 mb-3'>Active Members</p>
          {renderProgressBar((currentOrg?.members.length || 0).toString(), '∞', 'bg-blue-500', 'bg-blue-600')}
        </div>
      </Link>

      <Link
        to={`${ORG_ROUTE}/${currentOrg?.id}/storage`}
        className='text-sm text-slate-700 dark:text-slate-300 mb-3 hover:text-slate-900 dark:hover:text-white transition-colors'
      >
        <div className='bg-white dark:bg-slate-800/50 rounded-xl p-4 backdrop-blur-xs border border-slate-200/50 dark:border-slate-700/50 shadow-xs'>
          <div className='flex justify-between items-start mb-3'>
            <span className='p-2 rounded-lg bg-purple-50 dark:bg-purple-500/10 fill-none text-purple-600 dark:text-purple-400'>
              <IconHardDrive className='h-5 w-5 fill-transparent' />
            </span>
            <span className='text-xs text-slate-600 dark:text-slate-400'>
              {getBytesBySize(storageMetric?.used || 0, 'MB')}/{getBytesBySize(storageMetric?.total || 0, 'MB')}MB
            </span>
          </div>
          <p className='text-sm text-slate-700 dark:text-slate-300 mb-3'>Storage Used</p>
          {renderProgressBar(
            getBytesBySize(storageMetric?.used || 0, 'MB').toString(),
            getBytesBySize(storageMetric?.total || 0, 'MB').toString(),
            'bg-purple-500',
            'bg-purple-600'
          )}
        </div>
      </Link>

      <div className='bg-white dark:bg-slate-800/50 rounded-xl p-4 backdrop-blur-xs border border-slate-200/50 dark:border-slate-700/50 shadow-xs'>
        <div className='flex justify-between items-start mb-3'>
          <span className='p-2 rounded-lg bg-teal-50 dark:bg-teal-500/10 fill-none text-teal-600 dark:text-teal-400'>
            <Eye24 className='h-5 w-5' />
          </span>
          <span className='text-xs text-slate-600 dark:text-slate-400'>0/∞</span>
        </div>
        <p className='text-sm text-slate-700 dark:text-slate-300 mb-3'>Monthly Visitors</p>
        {renderProgressBar('0', '∞', 'bg-teal-500', 'bg-teal-600')}
      </div>
    </div>
  )
}

export default QuickStatsSection
